import { useEffect, useRef } from 'react';
import useIsDarkMode from '../hooks/useIsDarkMode';

const WIDGET_SCRIPT_SRC = 'https://cdn-static.trendlyne.com/static/js/webwidgets/tl-widgets.js';
const WIDGET_COLORS = 'posCol=00A25B&primaryCol=006AFF&negCol=EB3B00&neuCol=F7941E';

// technical-widget and checklist-widget render as a plain <iframe src=...> — safe
// to remount any number of times, and governed by trendlyne.com's own CSP since
// it's a genuine cross-origin navigation. swot-widget's own tl-widgets.js instead
// fetches raw HTML and injects a <script> straight into the host page's global
// scope, which throws a "has already been declared" SyntaxError the second time
// it runs in the same tab (e.g. navigating from one stock's analysis page to
// another) — and even sandboxed via srcDoc, that inline script is still governed
// by *our* CSP, not trendlyne's, requiring a growing allowlist for every host its
// page happens to pull in. So we load the same widget URL ourselves as a plain
// iframe instead, sidestepping both problems.
const SHARED_SCRIPT_WIDGETS = [
  { key: 'technical-widget', title: 'Technical Analysis' },
  { key: 'checklist-widget', title: 'Check Before You Buy' },
];

function widgetUrl(key, symbol) {
  return `https://trendlyne.com/web-widget/${key}/Poppins/${encodeURIComponent(symbol)}/?${WIDGET_COLORS}`;
}

// Trendlyne's tl-widgets.js scans the DOM for `.trendlyne-widgets` blocks and
// converts them the moment the script executes — it doesn't watch for later
// DOM changes. Re-appending a fresh <script> tag on every symbol change makes
// it re-scan and pick up the new blockquotes.
export default function TrendlyneWidgets({ symbol }) {
  const containerRef = useRef(null);
  const isDark = useIsDarkMode();
  const theme = isDark ? 'dark' : 'light';

  useEffect(() => {
    if (!symbol || !containerRef.current) return;
    const script = document.createElement('script');
    script.src = WIDGET_SCRIPT_SRC;
    script.async = true;
    script.charset = 'utf-8';
    containerRef.current.appendChild(script);
    return () => script.remove();
  }, [symbol, theme]);

  return (
    <div ref={containerRef} key={`${symbol}:${theme}`} className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-items-start">
      <div className="flex flex-col gap-2 w-full md:col-span-2">
        <div className="flex-1 rounded-lg border border-border bg-card p-4 overflow-x-auto w-full flex justify-center">
          <iframe
            title={`SWOT Analysis - ${symbol}`}
            src={widgetUrl('swot-widget', symbol)}
            style={{ width: '100%', maxWidth: 580, height: 420, border: 'none', background: 'transparent' }}
          />
        </div>
      </div>
      {SHARED_SCRIPT_WIDGETS.map(w => (
        <div key={w.key} className="flex flex-col gap-2 w-full">
          <div className="flex-1 rounded-lg border border-border bg-card p-4 overflow-x-auto w-full flex justify-center">
            <blockquote
              className="trendlyne-widgets"
              data-get-url={widgetUrl(w.key, symbol)}
              data-theme={theme}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
